/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { CalendarDate } from '@internationalized/date';
import cx from 'classnames';

import { useBreakpoint } from 'sarsaparilla';

import type {
    FlexDatePickerPopoverAlign,
    FlexDatePickerSize,
} from './FlexDatePicker.types';
import { DatePickerDrawer } from './DatePickerDrawer';
import { DatePickerPopover } from './DatePickerPopover';
import { SingleDatePickerContent } from './SingleDatePickerContent';
import { useSingleDatePickerDraft } from './useSingleDatePickerDraft';
import styles from './FlexDatePicker.module.scss';

export interface SingleDatePickerProps extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'onChange'
> {
    value: CalendarDate | null;
    onChange: (value: CalendarDate | null) => void;
    /** Screen-reader name for the trigger. Defaults to `Date`. */
    label?: string;
    /** Trigger height. Defaults to `md`. */
    size?: FlexDatePickerSize;
    /** Popover alignment. Defaults to `center`. */
    popoverAlign?: FlexDatePickerPopoverAlign;
}

/**
 * Single-date counterpart to FlexDatePicker: the same trigger, popover, and
 * drawer, with one calendar selection and no flexible-date options.
 * Sends no click events, matching the sarsa DatePicker it replaces.
 */
export function SingleDatePicker({
    value,
    onChange,
    label = 'Date',
    size = 'md',
    popoverAlign = 'center',
    className,
    ...rest
}: SingleDatePickerProps) {
    // The draft lives here, not in the wrappers, so it survives a resize that
    // swaps the popover for the drawer.
    const draft = useSingleDatePickerDraft({ value, onChange });
    const { isSmAndDown } = useBreakpoint();
    const Wrapper = isSmAndDown ? DatePickerDrawer : DatePickerPopover;

    return (
        <div className={cx(styles.FlexDatePicker, className)} {...rest}>
            <Wrapper
                shell={draft}
                size={size}
                popoverAlign={popoverAlign}
                title="Choose Date"
                triggerLabel={`${label}:`}
                clearLabel="Clear date"
            >
                <SingleDatePickerContent
                    draft={draft}
                    variant={isSmAndDown ? 'drawer' : 'popover'}
                />
            </Wrapper>
        </div>
    );
}
